import React, { forwardRef, useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { canIUse } from './../../../../utils'
import GlobalStateContext from '../../../../context'
import { STREAM_SOURCE_ENCODER, STREAM_SOURCE_STUDIO ,STREAM_SOURCE_URL} from '../../../../constants'
import { SubscribeButton } from '../../../../components/protected/Subscribe'
import { Link, Link as LinkIcon, Video, Check } from 'lucide-react'

const SelectStreamSource = forwardRef((props, ref) => {
  const history = useHistory();
  const [state, dispatch] = useContext(GlobalStateContext)
  const { io, features_accessible, active_streams } = state;
  const [selected, setSelected] = useState(props.stream?.source)
  const [canStudio, setCanStudio] = useState()
  const [canRTMP, setCanRTMP] = useState()

  useEffect(() => {
    setCanStudio(canIUse("webcam", features_accessible))
    setCanRTMP(canIUse("custom_rtmp_input", features_accessible))
  }, [])

  useEffect(() => {
    setSelected(props.stream?.source)
  }, [props.stream])

  const onSelect = (data) => {
    setSelected(data);
    props.onSelect(data);
  }

  const SourceCard = ({ source, icon: Icon, title, description, available, iconColor, iconBg }) => {
    const isSelected = selected === source;
    const isDisabled = !available;

    return (
      <button
        type="button"
        disabled={isDisabled}
        onClick={() => onSelect(source)}
        className={`
          relative p-6 rounded-xl border-2 transition-all duration-300 text-left w-full
          ${isSelected 
            ? 'border-blue-500 bg-blue-50 shadow-lg ring-2 ring-blue-200' 
            : isDisabled 
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
              : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md cursor-pointer'
          }
        `}
      >
        {/* Selection Indicator */}
        {isSelected && (
          <div className="absolute top-4 right-4 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
            <Check className="w-4 h-4 text-white" />
          </div>
        )}

        {/* Upgrade Badge */}
        {isDisabled && (
          <div className="absolute top-4 right-4">
            <span className="bg-orange-100 text-orange-600 text-xs font-medium px-2 py-1 rounded-full">
              Upgrade Required
            </span>
          </div>
        )}

        {/* Icon */}
        <div className={`w-16 h-16 ${iconBg} rounded-xl flex items-center justify-center mb-4 mx-auto`}>
          <Icon className={`w-8 h-8 ${iconColor}`} />
        </div>

        {/* Content */}
        <div className="text-center space-y-2">
          <h3 className={`text-lg font-semibold ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
            {title}
          </h3>
          <p className={`text-sm ${isSelected ? 'text-blue-600' : 'text-gray-600'}`}>
            {description}
          </p>
        </div>

        {/* Selected State Overlay */}
        {isSelected && (
          <div className="absolute inset-0 rounded-xl border-2 border-blue-500 pointer-events-none" />
        )}
      </button>
    );
  };

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Software/Encoder Option */}
        <SourceCard
          source={STREAM_SOURCE_ENCODER}
                          icon={LinkIcon}
          title="Software"
          description="Eg. OBS, vMix, Zoom"
          available={canRTMP}
          iconColor="text-purple-600"
          iconBg="bg-purple-100"
        />

        {/* Video Studio Option */}
        <SourceCard
          source={STREAM_SOURCE_STUDIO}
          icon={Video}
          title="Video Studio"
          description="Eg. phone camera"
          available={canStudio}
          iconColor="text-blue-600"
          iconBg="bg-blue-100"
        />

        {/* External Link Option */}
        <SourceCard
          source={STREAM_SOURCE_URL}
          icon={Link}
          title="External Link"
          description="Eg. youtube video, facebook"
          available={canStudio} // Using same permission as studio for now
          iconColor="text-green-600"
          iconBg="bg-green-100"
        />
      </div>

      {/* Help Text */}
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-500">
          Choose the source that best fits your streaming setup. You can change this later.
        </p>
      </div>
    </div>
  )
});

export default SelectStreamSource;